import { useQueryClient } from "@tanstack/react-query";

import { IPart } from "../models/Part";
import useDefaultCRUDHandlers from "../hooks/useDefaultCRUDHandlers";
import usePartMutations from "./usePartMutations";
import useOffers from "./useOffers";

/**
 * Shared logic for persisting an eBay listing title onto a part's offer.
 * Updates the existing offer when present, otherwise creates a default offer,
 * and flags the part as titleConfigured. Used by both the inline title builder
 * and the quick AI-title button so save behaviour stays consistent.
 */
export default function useSaveEbayOfferTitle() {
  const queryClient = useQueryClient();
  const { patchPart } = usePartMutations();
  const { createDefaultOffer, update } = useOffers();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Title");

  const resolveOffer = (part: IPart) => {
    const detailsCache: any = queryClient.getQueryData(["partDetails", part.id]);
    const partCache: any = queryClient.getQueryData(["parts", part.id]);
    const detailsPart = detailsCache?.data ?? detailsCache?.data?.data;
    const singlePart = partCache?.data ?? partCache?.data?.data;
    const latestOffers = detailsPart?.offers ?? singlePart?.offers ?? part.offers ?? [];
    return latestOffers?.[0] ?? part.offers?.[0];
  };

  const setTitleConfigured = (part: IPart) => {
    if (part.titleConfigured) return;

    patchPart.mutate(
      {
        id: part.id,
        titleConfigured: true,
      },
      {
        onSuccess: () => {
          part.titleConfigured = true;
        },
      }
    );
  };

  const saveTitle = (part: IPart | undefined, title: string, onSaved?: () => void) => {
    if (!part) return;

    const offer = resolveOffer(part);
    const updatedOffer = {
      ...offer,
      title,
    };

    if (offer) {
      update.mutate(updatedOffer, {
        ...saveHandlers,
        onSuccess: () => {
          saveHandlers.onSuccess();
          offer.title = title;
          setTitleConfigured(part);
          onSaved?.();
        },
      });
    } else {
      createDefaultOffer.mutate(
        { partId: part.id, body: updatedOffer },
        {
          ...saveHandlers,
          onSuccess: (data: any) => {
            saveHandlers.onSuccess();
            const createdOffer = data?.data?.offers?.[0];
            if (createdOffer) {
              part.offers = [createdOffer];
            } else {
              part.offers = [{ ...updatedOffer }] as any;
            }
            setTitleConfigured(part);
            onSaved?.();
          },
        }
      );
    }
  };

  // Same save as saveTitle but awaitable and silent, so bulk runs can report one summary
  // instead of a toast per part. Rejects when the offer write fails.
  const saveTitleAsync = async (part: IPart, title: string) => {
    const offer = resolveOffer(part);
    const updatedOffer = {
      ...offer,
      title,
    };

    if (offer) {
      await update.mutateAsync(updatedOffer);
      offer.title = title;
    } else {
      const data: any = await createDefaultOffer.mutateAsync({ partId: part.id, body: updatedOffer });
      const createdOffer = data?.data?.offers?.[0];
      part.offers = createdOffer ? [createdOffer] : ([{ ...updatedOffer }] as any);
    }

    setTitleConfigured(part);
  };

  return {
    saveTitle,
    saveTitleAsync,
    isSaving: update.isLoading || createDefaultOffer.isLoading,
  };
}
